import * as Sentry from "@sentry/react";

export interface StoredSentryUser {
  email?: string;
  id?: string;
  name?: string;
}

const STORAGE_KEY = "revpilot_user";

const canUseStorage = () => typeof window !== "undefined";

const parseStoredUser = (value: string | null): StoredSentryUser | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as StoredSentryUser;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: "authentication",
        context: "sentry-user-storage",
        source: "localStorage",
      },
      level: "info",
    });
    return null;
  }
};

export const getStoredSentryUser = (): StoredSentryUser | null => {
  if (!canUseStorage()) {
    return null;
  }

  return parseStoredUser(window.localStorage.getItem(STORAGE_KEY));
};

export const setStoredSentryUser = (user: StoredSentryUser | null): void => {
  if (!canUseStorage()) {
    return;
  }

  if (!user || (!user.email && !user.id && !user.name)) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: "authentication",
        context: "sentry-user-storage",
        source: "localStorage",
        action: "set",
      },
      level: "info",
    });
  }
};

export const clearStoredSentryUser = (): void => {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: "authentication",
        context: "sentry-user-storage",
        source: "localStorage",
        action: "clear",
      },
      level: "info",
    });
  }
};
